import './Dropzone.scss';

import React, { useCallback, useEffect, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import Icon from 'fe-design-base/atoms/Icon';
import NewText from 'fe-design-base/atoms/Text';
import Divider from 'fe-design-base/molecules/Divider';
import { useIsEmbedded } from 'hooks/useIsEmbedded';
import PropTypes from 'prop-types';

import Box from 'components/Box';
import Text from 'components/Text';

import { cxFunction } from 'util/className';

import { DEFAULT_ACCEPTED_FILE_TYPES } from './constants';

const DroppedFile = ({ name, onRemove, isEmbedded }) =>
  isEmbedded ? (
    <>
      <Divider />
      <Box spacebetween mv={8}>
        <Box row vcenter>
          <Icon color="mono400" size="medium" iconName="Document" />
          &nbsp;
          <NewText>{name}</NewText>
        </Box>
        <Icon
          size="medium"
          iconName="Delete"
          color="error300"
          onClick={onRemove}
        />
      </Box>
      <Divider />
    </>
  ) : (
    <Box row vcenter>
      <Text>{name}</Text>
      &nbsp;
      <Icon
        size="xsmall"
        iconName="Delete"
        color="info300"
        onClick={onRemove}
      />
    </Box>
  );

const Dropzone = ({
  cx,
  cxEl,
  onChange,
  acceptedFileTypes = DEFAULT_ACCEPTED_FILE_TYPES,
  titleI18n = 'dropzone.drop_file',
  supportedi18n = 'dropzone.supported_default',
  theme = 'gray',
  displayFile = true,
  setFieldValue, // Formik
  setFieldTouched, // Formik
  children,
}) => {
  const isEmbedded = useIsEmbedded();

  const [file, setFile] = useState(null);

  useEffect(() => {
    onChange(file);
  }, [file, onChange]);

  const onDrop = useCallback(
    acceptedFiles => {
      if (acceptedFiles.length === 0) return;
      if (setFieldValue) setFieldValue('file', acceptedFiles[0]);
      if (setFieldTouched) setFieldTouched('file', true);
      if (displayFile) setFile(acceptedFiles[0]);

      onChange(acceptedFiles[0]);
    },
    [setFieldValue, setFieldTouched, displayFile, onChange]
  );

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    multiple: false,
    accept: acceptedFileTypes,
  });

  const removeFile = useCallback(() => {
    setFile(null);
    if (setFieldValue) setFieldValue('file', null);
  }, [setFieldValue]);

  if (file) {
    return (
      <DroppedFile
        name={file.name}
        onRemove={removeFile}
        isEmbedded={isEmbedded}
      />
    );
  }

  return (
    <div {...getRootProps()} data-testid="dropzone">
      <Box className={cx(theme)} vcenter hcenter column h={130}>
        <input {...getInputProps()} data-testid="dropzone-input" />

        {/* Add children if we need custom or different content */}
        {children}
        <Box>
          <Text className={cxEl('title')} i18n={titleI18n} fs14 fw600 />
        </Box>
        <Box>
          <Text className={cxEl('subTitle')} i18n={supportedi18n} fs14 />
        </Box>
      </Box>
    </div>
  );
};

Dropzone.propTypes = {
  onChange: PropTypes.func.isRequired,
  acceptedFileTypes: PropTypes.object,
  supportedi18n: PropTypes.string,
  titleI18n: PropTypes.string,
};

export default cxFunction('Dropzone', Dropzone);
